import { useCallback, useMemo, useState } from "react";

import { type GetSalesChartReport_me_salesReportChart } from "@north-beam/nb-client-side";
import { PLATFORM_NORTHBEAM } from "@north-beam/nb-common";
import { TabView } from "@pages/sales/tab-table-section/tab-table-section";

import { useAdsetFilters, useCampaignFilters } from "../atoms";

export const useSalesChartReportDataFilter = (
  salesReport: GetSalesChartReport_me_salesReportChart | undefined,
  granularity: TabView,
) => {
  /**
   * Since we're filtering programmatically we have two sets of filters, the
   * generated{level}Filters which gets reset every time and the user{level}Filters
   * made by the user which persists until the user clears it or clicks on a parent
   * level which clears the  children level.
   */
  const [prevLevel, setPrevLevel] = useState<TabView>(granularity);

  const [userPlatformFilters, setUserPlatformFilters] = useState<string[]>([]);

  const platformFilters = useMemo(
    () => Array.from(new Set([...userPlatformFilters])),
    [userPlatformFilters],
  );
  const [campaignFilters, setUserCampaignFilters] = useCampaignFilters();
  const [adsetFilters, setUserAdsetFilters] = useAdsetFilters();

  const filteredRows = useMemo(() => {
    let rows = salesReport?.rows ?? [];

    const hasLevelAnyFilters = !(
      platformFilters.length === 0 &&
      campaignFilters.length === 0 &&
      adsetFilters.length === 0
    );
    const isPlatformTab = granularity === TabView.PLATFORM;

    if (isPlatformTab) {
      return rows;
    }

    if (hasLevelAnyFilters || !isPlatformTab) {
      // Filters rows by level
      rows = rows.filter(({ dimensions }) => {
        try {
          // If there are no platform filters, this is vacuously matched
          let hasPlatformMatch = true;
          if (platformFilters.length) {
            /* If there are platform filters, and the ad key includes that platform
               there is a match */
            if (platformFilters.includes(dimensions[PLATFORM_NORTHBEAM])) {
              hasPlatformMatch = true;

              /* If (not set) has been chosen, indicated by a '' platformFilter
                 and there are no dimensions
                 there is a match */
            } else if (
              platformFilters.includes("") &&
              dimensions[PLATFORM_NORTHBEAM] === undefined
            ) {
              hasPlatformMatch = true;

              // Otherwise, no match
            } else {
              hasPlatformMatch = false;
            }
          }

          if (granularity === TabView.CAMPAIGN && hasPlatformMatch) {
            return true;
          }

          if (granularity === TabView.ADSET && hasPlatformMatch) {
            return true;
          }

          if (granularity === TabView.AD && hasPlatformMatch) {
            return true;
          }

          return false;
        } catch (error) {
          console.error(error);
          return true;
        }
      });
    }

    return rows;
  }, [
    platformFilters,
    campaignFilters,
    adsetFilters,
    granularity,
    salesReport,
  ]);

  const clearLevelFilter = useCallback(
    (level: TabView) => {
      switch (level) {
        case TabView.PLATFORM:
          setUserPlatformFilters([]);
          setUserCampaignFilters([]);
          setUserAdsetFilters([]);
          break;
        case TabView.CAMPAIGN:
          setUserCampaignFilters([]);
          setUserAdsetFilters([]);
          break;
        case TabView.ADSET:
          setUserAdsetFilters([]);
          break;
      }
    },
    [setUserAdsetFilters, setUserCampaignFilters],
  );

  /**
   * selectedRows is an array of objectIds gets converted to an object to extract
   * the ad level names used to filter the table
   */
  const setObjectIdsToAdLevelFilters = useCallback(
    (selectedRows, level: TabView) => {
      const platformSet = new Set<string>(platformFilters);
      const campaignSet = new Set<string>(campaignFilters);
      const adSetSet = new Set<string>([]);

      switch (level) {
        case TabView.PLATFORM:
          platformSet.clear();
          setUserCampaignFilters([]);

          campaignSet.clear();
          setUserPlatformFilters([]);

          adSetSet.clear();
          setUserAdsetFilters([]);

          break;
        case TabView.CAMPAIGN:
          campaignSet.clear();
          setUserCampaignFilters([]);

          break;
        case TabView.ADSET:
          adSetSet.clear();
          setUserAdsetFilters([]);

          break;
      }

      selectedRows.forEach((rowKey: string) => {
        try {
          const descriptor = rowKey.split("\n");
          // Very naive check for now
          const isJsonParseableString = (str: string) =>
            str.startsWith("{") && str.endsWith("}");
          const objectId = descriptor.find(isJsonParseableString);
          if (objectId) {
            const parsedObjectId = JSON.parse(objectId);
            Object.entries(parsedObjectId.nbt).forEach(
              ([level, value]: any) => {
                switch (level) {
                  case TabView.CAMPAIGN:
                    campaignSet.add(value);
                    break;
                  case TabView.ADSET:
                    if (value === "") {
                      adSetSet.add(JSON.stringify(parsedObjectId.nbt));
                    } else {
                      adSetSet.add(value);
                    }
                    break;
                }
              },
            );
          } else {
            // This should be the platform after __root__ if it's not JSON serializable
            platformSet.add(descriptor[1]);
          }
        } catch (error) {
          console.error(error);
        }
      });

      const adsetsArr = Array.from(adSetSet);
      const campaignArr = Array.from(campaignSet);
      switch (prevLevel) {
        case TabView.PLATFORM:
          setUserPlatformFilters(Array.from(platformSet));
          break;
        case TabView.CAMPAIGN:
          setUserCampaignFilters(campaignArr);
          break;
        case TabView.ADSET:
        case TabView.AD:
          setUserAdsetFilters(adsetsArr);
          break;
      }

      setPrevLevel(level);
    },
    [
      platformFilters,
      campaignFilters,
      prevLevel,
      setUserCampaignFilters,
      setUserAdsetFilters,
    ],
  );

  return {
    filteredRows,
    clearLevelFilter,
    setObjectIdsToAdLevelFilters,
    salesTabTableSelectedCount: {
      platform: userPlatformFilters.length,
      campaign: campaignFilters.length,
      adset: adsetFilters.length,
    },
  };
};
